import React from "react";
import config from "../data/siteConfig";
import { shopPhotos } from "../data/gallery";
import { MandalaCorner, BlockPrintBg, BlockBand } from "./Decor";
import { GiLotus, GiCutDiamond, GiNecklace } from "react-icons/gi";
import { FaGem } from "react-icons/fa";

const vintage = shopPhotos[0]; // old founders photo

// little triangular photo-corner mount (old album style)
function PhotoCorner({ className }) {
  return (
    <span className={`absolute h-7 w-7 border-coffee-800/70 ${className}`} aria-hidden="true" />
  );
}

const journey = [
  {
    Icon: GiLotus,
    year: "1956",
    title: "The Beginning",
    text:
      "સોની રામચંદ્ર નેણસી (આઘોઈવાળા) opens a single counter on Main Road, Bhachau — a simple promise of pure gold and honest weight.",
  },
  {
    Icon: GiNecklace,
    year: "Through the Years",
    title: "A Family Craft",
    text:
      "The art of Kutchi goldsmithing — kundan, meena and fine filigree — passed hand to hand, generation to generation.",
  },
  {
    Icon: FaGem,
    year: "Today",
    title: "Kutch's Trusted Name",
    text:
      "BIS Hallmark 916 gold, handcrafted bridal sets and everyday elegance for the families of Bhachau and beyond.",
  },
];

export default function OurShop() {
  return (
    <section id="ourshop" className="relative overflow-hidden bg-[#efe2c6] py-20 md:py-28">
      <BlockPrintBg className="absolute inset-0 text-coffee-700" opacity={0.06} />
      <MandalaCorner className="absolute -left-6 -top-6 h-40 w-40 text-coffee-400/40" />
      <MandalaCorner className="absolute -right-6 bottom-2 h-40 w-40 -scale-x-100 text-coffee-400/40" />

      <div className="container-x relative">
        {/* ornate heading */}
        <div className="text-center">
          <span className="inline-flex items-center gap-2 text-gold-deep">
            <GiLotus />
            <span className="eyebrow">A Heritage Since 1956</span>
            <GiLotus />
          </span>
          <h2 className="mt-3 font-display text-3xl text-coffee-800 sm:text-5xl">
            Our Story, <span className="font-serif italic text-gold-deep">Etched in Gold</span>
          </h2>
          <div className="mt-4 flex items-center justify-center gap-3 text-gold">
            <span className="h-px w-16 bg-gradient-to-r from-transparent to-gold" />
            <GiCutDiamond />
            <span className="h-px w-16 bg-gradient-to-l from-transparent to-gold" />
          </div>
        </div>

        <div className="mt-16 grid items-center gap-12 lg:grid-cols-2">
          {/* large vintage photo — aged album frame with photo-corners + wax seal */}
          <figure className="reveal relative mx-auto w-full max-w-xl">
            <div className="relative rounded-sm bg-[#fbf6ea] p-3 shadow-vintage ring-1 ring-coffee-800/15">
              <div className="relative border border-coffee-800/30 p-2">
                <img
                  src={vintage.src}
                  alt={vintage.caption}
                  loading="lazy"
                  className="w-full object-cover [filter:sepia(0.45)_contrast(1.02)_brightness(1.02)]"
                />
                <PhotoCorner className="left-1 top-1 border-l-2 border-t-2" />
                <PhotoCorner className="right-1 top-1 border-r-2 border-t-2" />
                <PhotoCorner className="bottom-1 left-1 border-b-2 border-l-2" />
                <PhotoCorner className="bottom-1 right-1 border-b-2 border-r-2" />
              </div>
              <figcaption className="px-2 pb-1 pt-3 text-center font-serif text-lg italic text-coffee-600">
                {vintage.caption}
              </figcaption>
            </div>

            {/* wax-seal style Est. badge */}
            <div className="absolute -bottom-7 -right-5 flex h-24 w-24 flex-col items-center justify-center rounded-full border-2 border-gold bg-coffee-grad text-center text-gold-light shadow-vintage">
              <span className="text-[0.55rem] uppercase tracking-[0.25em]">Estd.</span>
              <b className="font-display text-2xl leading-none">1956</b>
              <span className="text-[0.5rem] uppercase tracking-[0.18em]">Bhachau</span>
            </div>
          </figure>

          {/* right side — vintage "Our Journey" timeline + family quote */}
          <div className="reveal">
            <span className="eyebrow text-gold-deep">Our Journey</span>
            <h3 className="mt-2 font-display text-2xl text-coffee-800 sm:text-3xl">
              From a single counter to a Kutchi landmark
            </h3>

            <ol className="relative mt-8">
              {journey.map((m, i) => (
                <li key={i} className="flex gap-5 pb-8 last:pb-0">
                  <div className="flex flex-col items-center">
                    <span className="z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-gold bg-coffee-grad text-lg text-gold-light shadow-soft">
                      <m.Icon />
                    </span>
                    {i < journey.length - 1 && (
                      <span className="mt-1 w-px grow border-l-2 border-dashed border-gold/40" />
                    )}
                  </div>
                  <div className="pb-1">
                    <span className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-gold-deep">
                      {m.year}
                    </span>
                    <h4 className="font-display text-xl text-coffee-800">{m.title}</h4>
                    <p className="mt-1 text-coffee-700/85">{m.text}</p>
                  </div>
                </li>
              ))}
            </ol>

            <figure className="mt-4 border-t border-gold/25 pt-6">
              <blockquote className="font-serif text-xl italic text-coffee-700">
                “Every ornament we craft carries a piece of Kutch — and a promise kept since 1956.”
              </blockquote>
              <figcaption className="gu mt-2 text-gold-deep">— {config.brand.nameGu}</figcaption>
            </figure>
          </div>
        </div>
      </div>

      <BlockBand className="absolute bottom-0 left-0 block h-5 w-full" />
    </section>
  );
}
